import {
  Show,
  SimpleShowLayout,
  useTranslate,
  FunctionField,
  ListContextProvider,
  useList
} from "react-admin";
import { useParams } from "react-router-dom";
import CustomHeaderActions from "./CustomHeaderActions";
import ShowAndEditDetails from "./ShowAndEditDetails";
import { MenuDetailsIcon } from "assets/Icons";
import CustomDataGrid from "./CustomDataGrid";
import ListDotsActions from "./ListDotsActions";
import { Link } from "react-router-dom";
import { ListItemButton } from "@mui/material";
import * as User from "providers/user";
import { useQuery } from "@apollo/client";
import { GET_SORTED_MENU } from "gql";

const ConditionalTextField = ({ menuSections }: { menuSections: any }) => {
  if (menuSections?.length > 0) {
    return (
      <>
        {menuSections.map((menuSection: any, index: number) => (
          <MenuSection
            key={menuSection.id}
            menuSection={menuSection}
            index={index}
          />
        ))}
      </>
    );
  }

  return null;
};

export default function MenuShow() {
  const translate = useTranslate();
  const params = useParams();

  const { data: menuData } = useQuery(GET_SORTED_MENU, {
    variables: {
      where: {
        id: params.id
      },
      orderBy: [
        {
          section_position: "asc"
        }
      ],
      menuSectionServicesOrderBy2: [
        {
          section_position: "asc"
        }
      ]
    },
    skip: !params.id,
    fetchPolicy: "no-cache"
  });

  return (
    <Show actions={<CustomHeaderActions title={translate("pagenames.menu")} />}>
      <SimpleShowLayout>
        <ShowAndEditDetails
          title={translate("menusections.menu_details")}
          view="show"
          icon={<MenuDetailsIcon />}
        />
        <FunctionField
          label={translate("services.name") + ":"}
          render={(record: any) => record.name}
          fullWidth
        />
        <ConditionalTextField menuSections={menuData?.menu?.menu_sections} />
      </SimpleShowLayout>
    </Show>
  );
}

const MenuSection = ({
  menuSection,
  index
}: {
  menuSection: any;
  index: number;
}) => {
  if (!menuSection) {
    return null;
  }

  return (
    <div className="bg-mono-primaryBgColor mb-4 rounded-lg pt-4">
      <div className="text-[14px] font-[400] text-mono-secondaryTextColor ml-4">
        Section {index + 1}:
      </div>
      <div className="text-[14px] font-[600] text-mono-primaryTextColor ml-4 mb-4">
        {menuSection.name}
      </div>
      <MenuSectionService
        menuSectionServices={menuSection.menu_section_services}
      />
    </div>
  );
};

const MenuSectionService = ({
  menuSectionServices
}: {
  menuSectionServices: any;
}) => {
  const translate = useTranslate();
  const { permissions } = User.useUser();
  const listContext = useList({ data: menuSectionServices, isLoading: false });
  if (menuSectionServices?.length === 0) {
    return null;
  }

  return (
    <ListContextProvider value={listContext}>
      <CustomDataGrid
        rowClick={(id: string, resource: any, record: any) =>
          `/dashboard/Service/${record?.service?.id}/show`
        }
        sx={{
          borderRadius: "0px",
          ".RaDatagrid-headerCell": {
            backgroundColor: "transparent",
            minWidth: "0px",
            fontSize: "14px",
            "@media screen and (max-width:1280px)": {
              fontSize: "12px"
            }
          },
          ".RaDatagrid-rowCell": {
            minWidth: "0px",
            width: "358px",
            padding: "13px 16px"
          },
          ".RaDatagrid-rowCell:last-child": {
            width: "40px"
          },
          ".RaDatagrid-rowCell span": {
            fontSize: "14px",
            width: "fit-content",
            "@media screen and (max-width:1280px)": {
              fontSize: "12px"
            }
          },
          ".RaDatagrid-row:first-child .MuiTableCell-root": {
            borderTop: "1px solid rgba(224, 224, 224, 1)"
          },
          ".RaDatagrid-row:last-child .MuiTableCell-root": {
            borderWidth: "0px"
          }
        }}
      >
        <FunctionField
          label={translate("services.name")}
          render={(record: any) => (
            <span className="text-[#16667A]">{record?.service?.name}</span>
          )}
        />
        <FunctionField
          label={translate("services.price")}
          render={(record: any) => `$${record?.service?.price}`}
        />
        <FunctionField
          label={translate("services.duration")}
          render={(record: any) => `${record?.service?.duration} mins`}
        />
        {!permissions.isArtist && (
          <FunctionField
            render={(record: any) => (
              <div className="flex justify-end">
                <ListDotsActions>
                  <Link to={`/dashboard/Service/${record?.service?.id}`}>
                    <ListItemButton
                      sx={{
                        fontSize: "14px",
                        fontFamily: "Inter",
                        "& .MuiListItemButton-root": {
                          fontSize: "14px",
                          fontFamily: "Inter"
                        }
                      }}
                    >
                      {translate("common.edit")}
                    </ListItemButton>
                  </Link>
                </ListDotsActions>
              </div>
            )}
          />
        )}
      </CustomDataGrid>
    </ListContextProvider>
  );
};
